import React from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, Check, FileText, Phone, Mail, MessageSquare, User, Briefcase, Calendar, Wallet, ChevronDown, ChevronRight, Loader2, Plus, FileSignature, ScrollText, Search, Download, Upload as UploadIcon, MessageCircle, CreditCard, Wrench, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { matters as mattersApi, intel as intelApi, occupations as occupationsApi, filenotes as filenotesApi, quickparts as quickpartsApi, matterArchive } from '@/lib/api';
import { CHECKLIST_186_DE } from '@/mock/mockData';
import VevoPanel from './VevoPanel';
import EsignPage from './EsignPage';
import TasksPage from './TasksPage';
import TimeTrackingPage from './TimeTrackingPage';
import RetainerCard from '@/components/RetainerCard';
import PathwayAdvisorCard from '@/components/PathwayAdvisorCard';
import AuditReactions from '@/components/AuditReactions';
import ApplicantTasksCard from '@/components/ApplicantTasksCard';
import MatterMessagingCard from '@/components/MatterMessagingCard';
import { WorkflowTracker, ApplicantDetailsCard, PointsWidget, AddFilenoteButton, InteractiveChecklist } from '@/components/MatterWorkflowWidgets';
import PostLodgementTab from '@/components/PostLodgementTab';

const STAGE_COLORS = {
  INTAKE: 'bg-slate-100 text-slate-700', ENGAGED: 'bg-blue-100 text-blue-700',
  IN_PREP: 'bg-amber-100 text-amber-700', LODGED: 'bg-indigo-100 text-indigo-700',
  DECIDED: 'bg-emerald-100 text-emerald-700',
};
const KIND_ICONS = { ai: Sparkles, doc: FileText, note: Phone, checklist: Check, sign: FileText, email: Mail, sms: MessageSquare };

export default function MatterDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = ['comms', 'checklist', 'docs', 'ai', 'filenotes', 'esign', 'intel', 'audit', 'tasks', 'time', 'points', 'postlodge'].includes(requestedTab) ? requestedTab : 'comms';
  const [matter, setMatter] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [openSections, setOpenSections] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([mattersApi.get(id), mattersApi.timeline(id)])
      .then(([m, ev]) => { setMatter(m); setEvents(ev); })
      .catch(() => setMatter(null))
      .finally(() => setLoading(false));
  }, [id]);
  React.useEffect(load, [load]);

  const toggle = (s) => setOpenSections({ ...openSections, [s]: !openSections[s] });

  if (loading) return <div className="flex h-full items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;
  if (!matter) return <div className="p-6 text-slate-500">Matter not found.</div>;

  return (
    <div className="p-6">
      <Link to="/app/matters" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1A3A5C]">
        <ArrowLeft className="h-4 w-4" /> All matters
      </Link>

      <div className="mt-4 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">{matter.applicant}</h1>
            <StageEditor matter={matter} onChanged={load} />
          </div>
          <div className="mt-1 text-sm text-slate-500">{matter.ref} · Subclass {matter.subclass}{matter.stream ? ` · ${matter.stream}` : ''} · {matter.country || '—'}{matter.sponsor_id ? <span data-testid="matter-sponsor-chip" className="ml-2 rounded-full border border-[#2ECC9A]/30 bg-[#2ECC9A]/10 px-2 py-0.5 text-[10px] font-medium text-[#0F766E]"><a href="/app/sponsors" className="hover:underline">Sponsor linked</a></span> : null}</div>
        </div>
        <div className="flex gap-2">
          <ArchiveButton matter={matter} onChanged={load} />
          <Button variant="outline" className="h-10 border-slate-200">Send to portal</Button>
          <Link to="/app/lodgement"><Button className="h-10 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">Open Lodgement</Button></Link>
        </div>
      </div>

      <div className="mt-6 grid gap-4 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500"><User className="h-3 w-3" /> Assigned</div>
          <div className="mt-1 text-sm font-medium text-[#1A3A5C]">{matter.assigned_to_name}</div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500"><Briefcase className="h-3 w-3" /> Occupation → body</div>
          <div className="mt-1 text-sm font-medium text-[#1A3A5C]">{matter.occupation ? `${matter.occupation} → ${matter.assessing_body}` : '—'}</div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500"><Calendar className="h-3 w-3" /> Next deadline</div>
          <div className="mt-1 text-sm font-medium text-[#1A3A5C]">{matter.next_deadline || '—'}</div>
          <div className="text-xs text-slate-500">{matter.deadline_label || ''}</div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500"><Wallet className="h-3 w-3" /> Trust balance</div>
          <div className="mt-1 font-mono text-sm font-medium text-[#1A3A5C]">A${(matter.trust_balance || 0).toLocaleString()}</div>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <WorkflowTracker matter={matter} onChanged={() => window.location.reload()} />
        <ApplicantDetailsCard matter={matter} onSaved={() => window.location.reload()} />
        <PathwayAdvisorCard matterId={matter.id} matterRef={matter.ref} />
        <ApplicantTasksCard matterId={matter.id} applicantId={matter.applicant_id} />
        <MatterMessagingCard matterRef={matter.ref} applicantPhone={matter.applicant_phone || matter.emergency_contact_phone || null} />
        <VevoPanel matterRef={matter.ref} />
        <RetainerCard matterId={matter.id} />
      </div>

      <Tabs defaultValue={initialTab} className="mt-6">
        <TabsList className="grid w-full grid-cols-12 md:w-fit">
          <TabsTrigger value="comms">Communications</TabsTrigger>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
          <TabsTrigger value="docs">Documents</TabsTrigger>
          <TabsTrigger value="filenotes" data-testid="matter-tab-filenotes">Filenotes</TabsTrigger>
          <TabsTrigger value="esign" data-testid="matter-tab-esign">eSign</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="matter-tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="time" data-testid="matter-tab-time">Time</TabsTrigger>
          <TabsTrigger value="points" data-testid="matter-tab-points">Points</TabsTrigger>
          <TabsTrigger value="postlodge" data-testid="matter-tab-postlodge">Post-lodge</TabsTrigger>
          <TabsTrigger value="ai">AI</TabsTrigger>
          <TabsTrigger value="intel" data-testid="matter-tab-intel">Migration intel</TabsTrigger>
          <TabsTrigger value="audit" data-testid="matter-tab-audit">Audit trail</TabsTrigger>
        </TabsList>

        <TabsContent value="comms" className="mt-6">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-100 p-4">
                  <div className="text-sm font-semibold text-[#1A3A5C]">Unified communications log</div>
                  <div className="text-xs text-slate-500">Live from database · Calls, notes, docs, checklist events</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {events.length === 0 && <div className="p-6 text-sm text-slate-500">No entries yet. Add one on the right.</div>}
                  {events.map((e) => {
                    const Icon = KIND_ICONS[e.kind] || FileText;
                    return (
                      <div key={e.id} className="flex gap-3 px-4 py-4">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2ECC9A]/10">
                          <Icon className="h-4 w-4 text-[#2ECC9A]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="font-medium text-[#1A3A5C]">{e.actor}</span>
                            <span>·</span>
                            <span>{new Date(e.at).toLocaleString()}</span>
                          </div>
                          <div className="mt-1 text-sm text-slate-700">{e.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <NoteComposer matterId={id} onAdded={load} />
          </div>
        </TabsContent>

        <TabsContent value="checklist" className="mt-6">
          <div className="mb-3 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-3 text-xs text-[#1A3A5C]">
            <span className="font-medium">DHA subclass {matter.subclass}</span> checklist. Tick each item as documents are received — items start unticked (no auto-progress).
          </div>
          <InteractiveChecklist
            matterId={matter.id}
            items={CHECKLIST_186_DE.flatMap((sec) => sec.items).map((it) => ({ id: it.id, label: it.label, source: it.source }))}
          />
        </TabsContent>

        <TabsContent value="docs" className="mt-6">
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Full document vault: <Link to="/app/documents" className="text-[#2ECC9A] hover:underline">open module →</Link>
          </div>
        </TabsContent>
        <TabsContent value="filenotes" className="mt-6">
          <div className="mb-3 flex justify-end">
            <AddFilenoteButton matterId={matter.id} onAdded={() => window.location.reload()} />
          </div>
          <FilenotesTab matterRef={matter.ref} />
        </TabsContent>
        <TabsContent value="points" className="mt-6" data-testid="matter-tab-content-points">
          <PointsWidget matterId={matter.id} />
        </TabsContent>
        <TabsContent value="postlodge" className="mt-6" data-testid="matter-tab-content-postlodge">
          <PostLodgementTab matterId={matter.id} />
        </TabsContent>
        <TabsContent value="esign" className="mt-6">
          <EsignPage matterRef={matter.ref} embedded />
        </TabsContent>
        <TabsContent value="tasks" className="mt-6">
          <TasksPage matterId={matter.id} matterRef={matter.ref} embedded />
        </TabsContent>
        <TabsContent value="time" className="mt-6">
          <TimeTrackingPage matterId={matter.id} matterRef={matter.ref} embedded />
        </TabsContent>
        <TabsContent value="ai" className="mt-6">
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            AI Copilot for this matter: <Link to="/app/copilot" className="text-[#2ECC9A] hover:underline">draft RFI, suggest occupation →</Link>
          </div>
        </TabsContent>
        <TabsContent value="intel" className="mt-6">
          <MatterIntelPanel matterId={matter.id} />
        </TabsContent>
        <TabsContent value="audit" className="mt-6">
          <AuditTrailPanel events={events} onRefresh={load} matterId={matter.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StageEditor({ matter, onChanged }) {
  const [saving, setSaving] = React.useState(false);
  const change = async (v) => {
    setSaving(true);
    try { await mattersApi.update(matter.id, { stage: v }); onChanged(); }
    finally { setSaving(false); }
  };
  return (
    <Select value={matter.stage} onValueChange={change} disabled={saving}>
      <SelectTrigger className="h-7 w-auto gap-2 border-none bg-transparent px-0 shadow-none">
        <Badge className={(STAGE_COLORS[matter.stage] || 'bg-slate-100 text-slate-700') + ' text-[10px] uppercase tracking-wider'}>{(matter.stage||'').replace('_',' ')}</Badge>
      </SelectTrigger>
      <SelectContent>
        {['INTAKE','ENGAGED','IN_PREP','LODGED','DECIDED'].map((s) => <SelectItem key={s} value={s}>{s.replace('_',' ')}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function NoteComposer({ matterId, onAdded }) {
  const [text, setText] = React.useState('');
  const [kind, setKind] = React.useState('note');
  const [saving, setSaving] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    setSaving(true);
    try { await mattersApi.addNote(matterId, { text, kind }); setText(''); onAdded?.(); }
    finally { setSaving(false); }
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="text-sm font-semibold text-[#1A3A5C]">Add to log</div>
        <form onSubmit={submit} className="mt-3 space-y-3">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="note">Phone / note</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="doc">Document event</SelectItem>
              <SelectItem value="checklist">Checklist change</SelectItem>
            </SelectContent>
          </Select>
          <Textarea rows={4} value={text} onChange={(e)=>setText(e.target.value)} placeholder="e.g. Called applicant — confirmed medical booking on 14 Aug." />
          <Button type="submit" disabled={saving} className="w-full gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add entry
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}


// Migration intel panel for a single matter — G-phase consolidation.
function MatterIntelPanel({ matterId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    intelApi.forMatter(matterId)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [matterId]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>;
  if (err) return <div className="rounded-md border border-red-200 bg-red-50 p-4 text-xs text-red-700">{err}</div>;
  if (!data) return null;

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="matter-intel-panel">
      <SkillsAssessmentWidget matter={data} />
      {data.latest_round && (
        <div className="rounded-md border border-sky-200 bg-sky-50/40 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-sky-800">Latest SkillSelect round</div>
          <div className="mt-1 text-sm text-[#1A3A5C]">Subclass {data.latest_round.subclass} · {data.latest_round.round_date}</div>
          <div className="mt-2 text-xs text-slate-600">
            Min points: <b>{data.latest_round.min_points}</b> · Cut-off DOE: {data.latest_round.cutoff_date || '—'} · Invitations: {(data.latest_round.invitations_issued || 0).toLocaleString()}
          </div>
        </div>
      )}
      {data.federal_quota && (
        <div className="rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1A3A5C]">Federal planning {data.program_year}</div>
          <div className="mt-1 text-sm text-[#1A3A5C]">{data.federal_quota.stream}</div>
          <div className="mt-2 text-2xl font-bold text-[#1A3A5C]">{(data.federal_quota.planning_level || 0).toLocaleString()}<span className="ml-1 text-xs text-slate-500">places</span></div>
        </div>
      )}
      {data.state_program && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4 md:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-800">{data.state_program.state} nomination</div>
          <div className="mt-1 text-sm text-[#1A3A5C]">190: {data.state_program.allocation_190?.toLocaleString()} · 491: {data.state_program.allocation_491?.toLocaleString()}</div>
          <div className="mt-1 text-xs text-slate-700">{data.state_program.eligibility_summary}</div>
        </div>
      )}
      {data.pathway && (
        <div className="rounded-md border border-[#1A3A5C]/20 bg-white p-4 md:col-span-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#1A3A5C]">Saved pathway analysis</div>
            <Badge variant="outline" className="border-slate-200 text-[10px]">{data.pathway.points_estimate?.total} pts</Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {(data.pathway.recommended || []).map((r) => (
              <Badge key={r.visa} variant="outline" className="border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[11px] text-[#1A3A5C]">{r.visa} · {r.eligible}</Badge>
            ))}
          </div>
        </div>
      )}
      <div className="md:col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Relevant updates</div>
        {data.updates.length === 0 ? (
          <div className="mt-2 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">No matching updates yet.</div>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
            {data.updates.map((u) => (
              <li key={u.id} className="flex items-start gap-3 p-3">
                <Badge variant="outline" className="shrink-0 border-slate-200 text-[10px] text-slate-600 capitalize">{u.source.replace('_', ' ')}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-[#1A3A5C]">{u.title}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2">{u.summary}</div>
                </div>
                {u.url && (<a href={u.url} target="_blank" rel="noopener noreferrer" className="ml-2 shrink-0 text-[10px] text-[#1A3A5C] hover:text-[#2ECC9A]">Source →</a>)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}



// Skills-assessment progress widget for the matter intel tab.
// Suggests the correct assessing body + timeframe based on occupation/ANZSCO.
function SkillsAssessmentWidget({ matter }) {
  const applicant = matter?.applicant || {};
  const occupation = applicant.occupation || applicant.nominated_occupation || '';
  const anzsco = applicant.anzsco || '';
  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!occupation && !anzsco) return;
    setLoading(true);
    occupationsApi.skillsAssessment({ occupation: occupation || undefined, anzsco: anzsco || undefined })
      .then(setState)
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, [occupation, anzsco]);

  if (!occupation && !anzsco) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/50 p-4 md:col-span-2" data-testid="skills-assessment-empty">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Skills assessment</div>
        <div className="mt-1 text-xs text-slate-500">Add an occupation or ANZSCO code to the matter to see the recommended assessing body.</div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-purple-200 bg-purple-50/40 p-4 md:col-span-2" data-testid="skills-assessment-widget">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-purple-800">Recommended assessing body</div>
          <div className="mt-1 text-sm text-[#1A3A5C]">
            {loading ? 'Looking up…' : state ? (
              <><b>{state.body}</b> · <span className="text-slate-600">{state.name}</span></>
            ) : 'No suggestion available.'}
          </div>
        </div>
        {state?.expected_weeks && (
          <Badge variant="outline" className="border-purple-300 bg-white text-[10px] text-purple-800" data-testid="skills-assessment-weeks">
            ~{state.expected_weeks} weeks
          </Badge>
        )}
      </div>
      {state?.notes && <div className="mt-2 text-[11px] text-slate-600">{state.notes}</div>}
      {state?.url && (
        <a href={state.url} target="_blank" rel="noopener noreferrer"
           data-testid="skills-assessment-source"
           className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-purple-800 hover:text-purple-900">
          Verify at source →
        </a>
      )}
      {occupation && (
        <div className="mt-2 text-[10px] text-slate-500">Client occupation: {occupation}{anzsco ? ` (ANZSCO ${anzsco})` : ''}</div>
      )}
    </div>
  );
}


function ArchiveButton({ matter, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const archived = matter.archived;
  const toggle = async () => {
    if (!window.confirm(archived ? 'Unarchive this matter?' : 'Archive this matter? It will be hidden from the default list.')) return;
    setBusy(true);
    try {
      if (archived) await matterArchive.unarchive(matter.ref);
      else await matterArchive.archive(matter.ref);
      onChanged?.();
    } finally { setBusy(false); }
  };
  return (
    <Button data-testid="matter-archive-btn" variant="outline" onClick={toggle} disabled={busy}
            className={`h-10 border-slate-200 ${archived ? 'text-emerald-700' : 'text-slate-600'}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (archived ? 'Unarchive' : 'Archive')}
    </Button>
  );
}

function FilenotesTab({ matterRef }) {
  const [notes, setNotes] = React.useState([]);
  const [qps, setQps] = React.useState([]);
  const [text, setText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([filenotesApi.list(matterRef), quickpartsApi.list()])
      .then(([n, q]) => { setNotes(n); setQps(q); })
      .finally(() => setLoading(false));
  }, [matterRef]);
  React.useEffect(load, [load]);

  const insertQp = (snippet) => {
    setText((t) => (t ? t + '\n\n' : '') + snippet);
  };
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await filenotesApi.create(matterRef, text.trim());
      setText('');
      load();
    } finally { setSaving(false); }
  };
  const remove = async (fid) => {
    if (!window.confirm('Delete this filenote?')) return;
    await filenotesApi.remove(fid);
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="filenotes-tab">
      <div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-[#1A3A5C]">Add filenote</div>
          <textarea data-testid="filenote-body" value={text} onChange={(e) => setText(e.target.value)} rows={4}
                    placeholder="Time-stamped record for this matter — call summary, DHA update, correspondence…"
                    className="mt-2 w-full rounded-md border border-slate-200 bg-white p-3 text-sm" />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {qps.length > 0 && 'Quickparts:'}
            </div>
            <Button data-testid="filenote-save" onClick={submit} disabled={saving || !text.trim()}
                    className="h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save filenote'}
            </Button>
          </div>
          {qps.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {qps.map((q) => (
                <button key={q.id} data-testid={`quickpart-${q.id}`} onClick={() => insertQp(q.snippet)}
                        title={q.snippet}
                        className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600 hover:bg-slate-100">
                  + {q.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {loading && <div className="text-xs text-slate-400"><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>}
          {!loading && notes.length === 0 && <div className="rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">No filenotes yet</div>}
          {notes.map((n) => (
            <Card key={n.id} className="border-slate-200" data-testid={`filenote-${n.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {(n.author_name || n.author_email) } · {new Date(n.created_at).toLocaleString('en-AU')}
                  </div>
                  <button onClick={() => remove(n.id)}
                          className="text-[10px] text-slate-400 hover:text-red-600">Delete</button>
                </div>
                <div className="mt-1 whitespace-pre-wrap text-xs text-[#1A3A5C]">{n.body_text}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card className="border-slate-200 h-fit">
        <CardContent className="p-4">
          <div className="text-xs font-semibold text-[#1A3A5C]">About filenotes</div>
          <p className="mt-1 text-[11px] text-slate-500">
            Filenotes are time-stamped records attached to this matter, used for
            OMARA compliance evidence, client dispute defence, and internal handover.
            Quickparts are reusable snippets your firm inserts one click.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


const AUDIT_KIND_META = {
  note:       { icon: MessageCircle,  label: 'Note',        tone: 'bg-slate-100 text-slate-700' },
  call:       { icon: Phone,          label: 'Call',        tone: 'bg-blue-100 text-blue-700' },
  email:      { icon: Mail,           label: 'Email',       tone: 'bg-indigo-100 text-indigo-700' },
  document:   { icon: FileText,       label: 'Document',    tone: 'bg-cyan-100 text-cyan-700' },
  checklist:  { icon: Check,          label: 'Checklist',   tone: 'bg-emerald-100 text-emerald-700' },
  letter:     { icon: ScrollText,     label: 'Letter',      tone: 'bg-violet-100 text-violet-700' },
  esign:      { icon: FileSignature,  label: 'eSign',       tone: 'bg-emerald-100 text-emerald-700' },
  question:   { icon: MessageSquare,  label: 'Question',    tone: 'bg-amber-100 text-amber-800' },
  payment:    { icon: CreditCard,     label: 'Payment',     tone: 'bg-emerald-100 text-emerald-700' },
  lodgement:  { icon: UploadIcon,     label: 'Lodgement',   tone: 'bg-blue-100 text-blue-700' },
  system:     { icon: Wrench,         label: 'System',      tone: 'bg-slate-100 text-slate-500' },
  ai:         { icon: Sparkles,       label: 'AI',          tone: 'bg-purple-100 text-purple-700' },
  sign:       { icon: FileSignature,  label: 'Sign',        tone: 'bg-emerald-100 text-emerald-700' },
  sms:        { icon: MessageSquare,  label: 'SMS',         tone: 'bg-orange-100 text-orange-700' },
};

function highlightMentions(text) {
  if (!text) return text;
  // Split by @handle tokens (matching backend _MENTION_RE)
  const parts = String(text).split(/(@[a-zA-Z][\w.\-]{1,63})/g);
  return parts.map((p, i) =>
    /^@[a-zA-Z]/.test(p)
      ? <span key={i} className="rounded bg-[#2ECC9A]/12 px-1 font-medium text-[#0F766E]">{p}</span>
      : p,
  );
}

function AuditTrailPanel({ events, onRefresh, matterId }) {
  const [kindFilter, setKindFilter] = React.useState('all');
  const [q, setQ] = React.useState('');
  const [actorFilter, setActorFilter] = React.useState('all');
  const [addOpen, setAddOpen] = React.useState(false);
  const [addKind, setAddKind] = React.useState('note');
  const [addText, setAddText] = React.useState('');
  const [addFiles, setAddFiles] = React.useState([]);
  // Reactions map { [eventId]: { up, query, me, queries } }
  const [reactions, setReactions] = React.useState({});
  const loadReactions = React.useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      const { auditReactions: reactApi } = await import('@/lib/api');
      const r = await reactApi.bulk(ids);
      setReactions(r.by_event || {});
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => {
    const ids = events.map((e) => e.id).filter(Boolean);
    loadReactions(ids);
  }, [events, loadReactions]);
  const [saving, setSaving] = React.useState(false);

  const saveNote = async () => {
    if (!addText.trim() || !matterId) return;
    setSaving(true);
    try {
      if (addFiles.length > 0) {
        await mattersApi.addNoteWithAttachments(matterId, {
          text: addText.trim(), kind: addKind, files: addFiles,
        });
      } else {
        await mattersApi.addNote(matterId, { text: addText.trim(), kind: addKind });
      }
      setAddOpen(false);
      setAddText('');
      setAddKind('note');
      setAddFiles([]);
      onRefresh?.();
    } catch (e) {
      void e;
    } finally { setSaving(false); }
  };

  const kinds = React.useMemo(() => Array.from(new Set(events.map((e) => e.kind).filter(Boolean))).sort(), [events]);
  const actors = React.useMemo(() => Array.from(new Set(events.map((e) => e.actor).filter(Boolean))).sort(), [events]);

  const filtered = React.useMemo(() => {
    const qLc = q.trim().toLowerCase();
    return events.filter((e) => {
      if (kindFilter !== 'all' && e.kind !== kindFilter) return false;
      if (actorFilter !== 'all' && e.actor !== actorFilter) return false;
      if (qLc && !`${e.text || ''} ${e.actor || ''}`.toLowerCase().includes(qLc)) return false;
      return true;
    });
  }, [events, kindFilter, q, actorFilter]);

  // Group by ISO date (yyyy-mm-dd) for a nice day-by-day audit stream
  const grouped = React.useMemo(() => {
    const g = {};
    filtered.forEach((e) => {
      const day = (e.at || '').slice(0, 10) || 'unknown';
      if (!g[day]) g[day] = [];
      g[day].push(e);
    });
    return Object.entries(g).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered]);

  const kindCounts = React.useMemo(() => {
    const c = {};
    events.forEach((e) => { c[e.kind] = (c[e.kind] || 0) + 1; });
    return c;
  }, [events]);

  const exportCsv = () => {
    const header = ['when', 'kind', 'actor', 'text'];
    const rows = filtered.map((e) => [e.at, e.kind, e.actor, (e.text || '').replace(/"/g, '""')]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="audit-trail-panel">
      <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="audit-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search action text or actor…"
            className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-2 text-xs"
          />
        </div>
        <select
          data-testid="audit-kind-filter"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
        >
          <option value="all">All kinds ({events.length})</option>
          {kinds.map((k) => (
            <option key={k} value={k}>{AUDIT_KIND_META[k]?.label || k} ({kindCounts[k]})</option>
          ))}
        </select>
        <select
          data-testid="audit-actor-filter"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
        >
          <option value="all">All actors</option>
          {actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Button data-testid="audit-add-note" onClick={() => setAddOpen(true)}
                size="sm" className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
          <Plus className="h-3 w-3" /> Add note
        </Button>
        <Button data-testid="audit-refresh" onClick={onRefresh} size="sm" variant="outline" className="h-9 gap-1 border-slate-200 text-xs">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
        <Button data-testid="audit-export-csv" onClick={exportCsv} size="sm" variant="outline"
                className="h-9 gap-1 border-slate-200 text-xs" disabled={filtered.length === 0}>
          <Download className="h-3 w-3" /> CSV
        </Button>
      </div>

      <div className="text-[11px] text-slate-500" data-testid="audit-count">
        Showing <b className="text-[#1A3A5C]">{filtered.length}</b> of {events.length} events
        {kindFilter !== 'all' && ` · filtered by ${AUDIT_KIND_META[kindFilter]?.label || kindFilter}`}
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed border-slate-200"><CardContent className="p-8 text-center text-xs text-slate-500">
          {events.length === 0
            ? 'No audit events yet. Every action taken on this matter will appear here.'
            : 'No events match the current filters.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, dayEvents]) => (
            <div key={day} data-testid={`audit-day-${day}`}>
              <div className="mb-1 flex items-center gap-2">
                <div className="h-px flex-1 bg-slate-200" />
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {day === 'unknown' ? 'Unknown date' : new Date(day + 'T00:00:00Z').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  <span className="ml-2 text-slate-400">· {dayEvents.length}</span>
                </div>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {dayEvents.map((e, i) => {
                  const meta = AUDIT_KIND_META[e.kind] || AUDIT_KIND_META.system;
                  const Icon = meta.icon;
                  return (
                    <div key={e.id || `${day}-${i}`}
                         data-testid={`audit-event-${e.id || i}`}
                         className={`flex gap-3 px-4 py-3 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${meta.tone}`}>
                            {meta.label}
                          </span>
                          <span className="font-medium text-[#1A3A5C]">{e.actor}</span>
                          <span>·</span>
                          <span>{new Date(e.at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                          {e.mentions && e.mentions.length > 0 && (
                            <span data-testid={`audit-event-mentions-${e.id || i}`}
                                  className="ml-1 text-[9px] text-[#0F766E]">
                              @ {e.mentions.map((m) => m.name).join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap">
                          {highlightMentions(e.text)}
                        </div>
                        {e.attachments && e.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1" data-testid={`audit-event-attachments-${e.id || i}`}>
                            {e.attachments.map((att) => (
                              <a
                                key={att.id || att.gridfs_id}
                                href={mattersApi.attachmentUrl(matterId, att.gridfs_id)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`audit-attachment-${att.gridfs_id}`}
                                className="inline-flex max-w-[240px] items-center gap-1 truncate rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] text-[#1A3A5C] hover:border-[#2ECC9A] hover:text-[#0F766E]"
                                title={att.filename}
                              >
                                <FileText className="h-3 w-3 shrink-0" />
                                <span className="truncate">{att.filename}</span>
                                <span className="ml-1 shrink-0 text-slate-400">{Math.round((att.size_bytes || 0) / 1024)}KB</span>
                              </a>
                            ))}
                          </div>
                        )}
                        {e.meta && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-[10px] text-slate-400 hover:text-[#1A3A5C]">metadata</summary>
                            <pre className="mt-1 max-w-full overflow-auto rounded bg-slate-50 p-2 text-[10px] text-slate-600">{JSON.stringify(e.meta, null, 2)}</pre>
                          </details>
                        )}
                        {e.id && (
                          <AuditReactions
                            eventId={e.id}
                            counts={reactions[e.id]}
                            onChange={(c) => setReactions((s) => ({ ...s, [e.id]: c }))}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent data-testid="audit-add-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
              <Plus className="h-4 w-4 text-[#2ECC9A]" /> Add a note to the audit trail
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Kind</label>
              <select
                data-testid="audit-add-kind"
                value={addKind}
                onChange={(e) => setAddKind(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
              >
                {['note', 'call', 'email', 'sms', 'ai', 'checklist', 'document', 'letter', 'esign', 'question', 'payment', 'lodgement', 'system'].map((k) => (
                  <option key={k} value={k}>
                    {AUDIT_KIND_META[k]?.label || k}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-[10px] text-slate-400">
                Tag the note so future filters group it correctly (e.g. a &quot;call&quot; logs a client phone conversation).
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Note</label>
              <Textarea
                data-testid="audit-add-text"
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                rows={4}
                placeholder="e.g. Client called to confirm timeline — no changes to lodgement plan. Loop in @teammate."
                className="mt-1 text-xs"
              />
              <div className="mt-1 text-[10px] text-slate-400">
                Tip: type <code className="rounded bg-slate-100 px-1">@name</code> to ping a teammate — they&apos;ll get an in-app alert.
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Attachments (optional, up to 5 · 10 MB each)</label>
              <label
                data-testid="audit-add-files-drop"
                className="mt-1 flex min-h-[36px] cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500 hover:border-[#2ECC9A] hover:text-[#0F766E]"
              >
                <Plus className="h-3 w-3" />
                <span className="flex-1 truncate">
                  {addFiles.length === 0 ? 'Screenshot, PDF, call recording, chat export…' : `${addFiles.length} file${addFiles.length === 1 ? '' : 's'} attached`}
                </span>
                <input
                  type="file"
                  multiple
                  data-testid="audit-add-files-input"
                  onChange={(e) => setAddFiles(Array.from(e.target.files || []).slice(0, 5))}
                  className="hidden"
                />
              </label>
              {addFiles.length > 0 && (
                <div className="mt-1 space-y-0.5" data-testid="audit-add-files-preview">
                  {addFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-[10px]">
                      <span className="truncate">{f.name} · {Math.round(f.size / 1024)} KB</span>
                      <button
                        type="button"
                        onClick={() => setAddFiles(addFiles.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="remove"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(false)} disabled={saving}
                    className="h-8 text-xs">Cancel</Button>
            <Button
              data-testid="audit-add-save"
              onClick={saveNote}
              disabled={saving || !addText.trim()}
              size="sm"
              className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
